import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Slide,
  Typography,
  useTheme,
} from "@mui/material";
import { useInfiniteQuery } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, X } from "lucide-react";
import SparklesGradientIcon from "@/assets/icons/SparklesGradientIcon";
import {
  getByAgentMessages,
  WhatsappConversationByAgentPage,
} from "@/modules/lead-page/api/lead-page.v2.api";
import { WhatsappConversationListItem } from "@/modules/lead-page/dtos/index-v2";
import WhatsappMessageThread from "@/modules/lead-page/new/components/activity/whatsapp-message-thread";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const formatLabel = (val: string) =>
  val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const CARD_RADIUS = "12px";
/* How far above the top edge the next page starts loading — far enough that
   the fetch usually lands before even a fast flick-scroll reaches the top, so
   the reader never stalls against the loader. */
const PREFETCH_MARGIN_PX = 1000;
/* Space permanently reserved for the older-messages spinner. */
const LOADER_SLOT_HEIGHT_PX = 24;

/* -------------------------------------------------------------------------- */
/*  Props                                                                      */
/* -------------------------------------------------------------------------- */

interface Props {
  journeyUid: string;
  agentUid: string;
  agentName: string | null;
  conversations: WhatsappConversationListItem[];
  hideHeader?: boolean;
  onOpenSummary?: () => void;
  externalSummaryOpen?: boolean;
  onExternalSummaryClose?: () => void;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

const WhatsappThreadPanel = ({
  journeyUid,
  agentUid,
  agentName,
  conversations,
  hideHeader = false,
  externalSummaryOpen,
  onExternalSummaryClose,
}: Props) => {
  const theme = useTheme();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  /* Distance from the bottom of the thread, captured when an older page is
     requested. Prepends only add content above the reader, so restoring this
     distance afterwards puts them back on the same message — and unlike a
     scrollHeight delta it stays correct no matter what else above them
     changed size in between (loader slot, avatars, waveforms). */
  const anchorFromBottomRef = useRef<number | null>(null);
  /* Set the moment a fetch is requested, not when React re-renders — the
     observer can fire several times before state catches up. */
  const isFetchingOlderRef = useRef(false);
  const didInitialScrollRef = useRef(false);
  const [internalSummaryOpen, setInternalSummaryOpen] = useState(false);
  const summaryOpen = externalSummaryOpen ?? internalSummaryOpen;
  const setSummaryOpen = (open: boolean) => {
    if (externalSummaryOpen !== undefined) {
      if (!open) onExternalSummaryClose?.();
    } else {
      setInternalSummaryOpen(open);
    }
  };

  /* ---- Data fetching ---- */
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery(
      ["by-agent-messages", journeyUid, agentUid],
      ({ pageParam }: { pageParam?: string }) =>
        getByAgentMessages(journeyUid, agentUid, pageParam),
      {
        getNextPageParam: (lastPage: WhatsappConversationByAgentPage) =>
          lastPage.nextCursor ?? undefined,
        refetchOnWindowFocus: false,
      },
    );

  /* ---- Flatten messages (oldest first) ----
     Page 0 is the newest batch; each subsequent page (fetched via nextCursor)
     is older. Reverse the page order so older messages land at the top and the
     newest at the bottom, matching a natural chat thread. */
  const allMessages = useMemo(
    () =>
      [...(data?.pages ?? [])]
        .reverse()
        .flatMap((p: WhatsappConversationByAgentPage) => p?.messages ?? [])
        .filter(Boolean),
    [data],
  );

  /* ---- Request the next older page ----
     Records the anchor and marks the fetch in flight synchronously, so repeat
     calls in the same tick collapse into one request. */
  const loadOlder = useCallback(() => {
    const root = chatContainerRef.current;
    if (!root || isFetchingOlderRef.current || !hasNextPage) return;
    isFetchingOlderRef.current = true;
    anchorFromBottomRef.current = root.scrollHeight - root.scrollTop;
    fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  /* ---- Load older messages when the top sentinel scrolls into view ----
     The observer is created once and reads live values through refs. Rebuilding
     it per render is what made the thread flicker: `observe()` re-fires the
     callback immediately with the current intersection state, so every fetch
     that toggled `isFetchingNextPage` re-triggered the next one and pages
     streamed in as fast as they could land. */
  const loadOlderRef = useRef(loadOlder);
  /* Layout effect, and declared before the scroll-positioning one below: the
     ref must hold this render's closure (fresh `hasNextPage`) by the time the
     positioning effect calls it. */
  useLayoutEffect(() => {
    loadOlderRef.current = loadOlder;
  }, [loadOlder]);

  useEffect(() => {
    const root = chatContainerRef.current;
    const target = topSentinelRef.current;
    if (!root || !target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadOlderRef.current();
      },
      { root, rootMargin: `${PREFETCH_MARGIN_PX}px`, threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  /* ---- Recover if a fetch ends without new messages (error, empty page) ---- */
  useEffect(() => {
    if (!isFetchingNextPage) isFetchingOlderRef.current = false;
  }, [isFetchingNextPage]);

  /* ---- Reset scroll state when switching conversation target ---- */
  useEffect(() => {
    didInitialScrollRef.current = false;
    anchorFromBottomRef.current = null;
    isFetchingOlderRef.current = false;
  }, [journeyUid, agentUid]);

  /* ---- Position the thread:
     - first load   → jump to the newest message (bottom)
     - older loaded → restore the anchor so the view doesn't move.
     Then top up deliberately, one page per commit, while the top of the thread
     is still inside the trigger zone — the sentinel does not move when content
     is added below it, so the observer alone would not fire again. */
  useLayoutEffect(() => {
    const el = chatContainerRef.current;
    if (!el || allMessages.length === 0) return;

    if (anchorFromBottomRef.current != null) {
      el.scrollTop = el.scrollHeight - anchorFromBottomRef.current;
      anchorFromBottomRef.current = null;
      isFetchingOlderRef.current = false;
      if (el.scrollTop <= PREFETCH_MARGIN_PX) loadOlderRef.current();
      return;
    }

    if (!didInitialScrollRef.current) {
      el.scrollTop = el.scrollHeight;
      didInitialScrollRef.current = true;
      /* A short first page can leave the sentinel permanently inside the
         trigger zone. The observer only fires on crossings, and its mount-time
         call ran before any page (or `hasNextPage`) existed — so top up here,
         the same way the prepend branch above does. */
      if (el.scrollTop <= PREFETCH_MARGIN_PX) loadOlderRef.current();
    }
  }, [allMessages.length]);

  /* ---- Summary data from most recent conversation ---- */
  const latestConversation = conversations[0] ?? null;
  const analysis = latestConversation?.latestAnalysis ?? null;
  const summary = latestConversation?.summary ?? null;
  const outcome = analysis?.outcome ?? null;
  const redFlags = analysis?.redFlags ?? [];
  const keyPoints = analysis?.keyPoints ?? [];
  const requiresEscalation = analysis?.requiresEscalation ?? false;
  const escalationReason = analysis?.escalationReason ?? null;
  const escalations: string[] =
    requiresEscalation && escalationReason ? [escalationReason] : [];

  /* ---- Render ---- */
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* ── Header ── */}
      {!hideHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.nawy.spacing.sm,
            minHeight: 60,
            px: theme.nawy.spacing.md,
            py: theme.nawy.spacing.sm,
            borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.headline.xs,
                color: theme.nawy.colors.semantic.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {agentName ?? "Agent"}
            </Typography>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.tertiary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              WhatsApp
            </Typography>
          </Box>

        </Box>
      )}

      {/* ── Message thread ── */}
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          // Scroll position is managed manually on prepend — the browser's
          // native scroll anchoring double-compensates and makes the thread
          // drift on its own.
          overflowAnchor: "none",
          // keep the page behind from taking over the scroll at either end
          overscrollBehavior: "contain",
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.xs,
          p: theme.nawy.spacing.md,
          // the thread paints its own card — matches the lead page activity
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: theme.nawy.colors.semantic.border.secondary,
            borderRadius: 3,
          },
        }}
      >
        {/* Top sentinel — always mounted so the observer can attach on mount
            and stay attached for the life of the panel */}
        {/* MUI sx treats numeric height ≤ 1 as a percentage — keep the unit */}
        <Box ref={topSentinelRef} sx={{ height: "1px", flexShrink: 0 }} aria-hidden />

        {/* Older-messages indicator. It stays put for as long as history
            remains rather than following each fetch, so it never blinks and
            never changes the thread's height as pages arrive. */}
        {hasNextPage && allMessages.length > 0 && (
          <Box
            role="status"
            aria-label="Loading older messages"
            sx={{
              height: LOADER_SLOT_HEIGHT_PX,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <CircularProgress
              size={16}
              thickness={4}
              sx={{ color: theme.nawy.colors.semantic.text.tertiary }}
            />
          </Box>
        )}

        {/* Initial loading */}
        {isLoading && allMessages.length === 0 && (
          <Box
            role="status"
            aria-live="polite"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: theme.nawy.spacing.sm,
              py: theme.nawy.spacing.xl,
              flex: 1,
            }}
          >
            <CircularProgress size={28} />
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.tertiary,
              }}
            >
              Loading messages...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {isError && (
          <Box
            role="alert"
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: theme.nawy.spacing.xs,
              py: theme.nawy.spacing.xl,
              textAlign: "center",
            }}
          >
            <AlertTriangle
              size={28}
              color={theme.nawy.colors.semantic.indicator.red}
              aria-hidden
            />
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.md.semibold,
                color: theme.nawy.colors.semantic.text.primary,
              }}
            >
              Couldn&apos;t load messages
            </Typography>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.tertiary,
              }}
            >
              Something went wrong. Please try again.
            </Typography>
          </Box>
        )}

        {/* Messages — same thread component the lead page activity uses */}
        {!isLoading && !isError && (
          <WhatsappMessageThread messages={allMessages} fillHeight />
        )}
      </Box>

      {/* ── Summary overlay ── */}
      <Slide direction="left" in={summaryOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 10,
            backgroundColor: theme.nawy.colors.semantic.layer.primary,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Overlay header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.nawy.spacing.sm,
              height: 52,
              px: theme.nawy.spacing.md,
              borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <SparklesGradientIcon size={18} />
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.headline.xs,
                  color: theme.nawy.colors.semantic.text.primary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Conversation Summary
              </Typography>
            </Box>
            <IconButton
              onClick={() => setSummaryOpen(false)}
              aria-label="Close summary"
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                color: theme.nawy.colors.semantic.text.primary,
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: theme.nawy.colors.semantic.layer.secondary,
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Box>

          {/* Overlay content */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.sm,
              p: theme.nawy.spacing.md,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                background: theme.nawy.colors.semantic.border.secondary,
                borderRadius: 3,
              },
            }}
          >
            {/* No analysis available */}
            {!analysis && !summary && (
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                  color: theme.nawy.colors.semantic.text.tertiary,
                  textAlign: "center",
                  py: theme.nawy.spacing.lg,
                }}
              >
                No summary available for this agent.
              </Typography>
            )}

            {/* Summary text */}
            {summary && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  px: theme.nawy.spacing.sm,
                  py: theme.nawy.spacing.xs,
                  borderRadius: CARD_RADIUS,
                  backgroundColor: theme.nawy.colors.primitives.white[200],
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 400,
                    lineHeight: "16px",
                    color: theme.nawy.colors.semantic.text.tertiary,
                    whiteSpace: "nowrap",
                  }}
                >
                  Summary
                </Typography>
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.md.regular,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {summary}
                </Typography>
              </Box>
            )}

            {/* Outcome chip */}
            {outcome && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  p: "12px",
                  borderRadius: CARD_RADIUS,
                  backgroundColor: theme.nawy.colors.primitives.white[200],
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 400,
                    lineHeight: "16px",
                    color: theme.nawy.colors.semantic.text.secondary,
                    whiteSpace: "nowrap",
                  }}
                >
                  Outcome
                </Typography>
                <Chip
                  label={formatLabel(outcome)}
                  size="small"
                  sx={{
                    fontSize: 11,
                    fontWeight: 400,
                    lineHeight: "16px",
                    backgroundColor: theme.nawy.colors.semantic.indicator.blue,
                    color: theme.nawy.colors.semantic.indicator.onColor,
                    borderRadius: "4px",
                    height: "auto",
                    "& .MuiChip-label": {
                      px: theme.nawy.spacing.xs,
                      py: "2px",
                    },
                  }}
                />
              </Box>
            )}

            {/* Key points */}
            {keyPoints && keyPoints.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  p: "12px",
                  borderRadius: CARD_RADIUS,
                  backgroundColor: theme.nawy.colors.primitives.white[200],
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 400,
                    lineHeight: "16px",
                    color: theme.nawy.colors.semantic.text.tertiary,
                  }}
                >
                  Key points
                </Typography>
                {keyPoints.map((point, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: theme.nawy.colors.semantic.indicator.navy,
                        flexShrink: 0,
                        mt: "6px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "18px",
                        color: theme.nawy.colors.semantic.text.primary,
                      }}
                    >
                      {point}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Escalation banner */}
            {escalations.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  p: "12px",
                  borderRadius: CARD_RADIUS,
                  backgroundColor: "#feeceb",
                }}
              >
                {escalations.map((msg, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        backgroundColor: theme.nawy.colors.semantic.indicator.red,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      <Typography
                        sx={{
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        !
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "18px",
                        color: theme.nawy.colors.semantic.text.primary,
                      }}
                    >
                      {msg}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Red flags */}
            {redFlags && redFlags.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  p: "12px",
                  borderRadius: CARD_RADIUS,
                  backgroundColor:
                    theme.nawy.colors.semantic.layer.extension.warningLight,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 400,
                    lineHeight: "16px",
                    color: theme.nawy.colors.semantic.text.tertiary,
                  }}
                >
                  Red flags
                </Typography>
                {redFlags.map((flag, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    <AlertCircle
                      size={16}
                      color={theme.nawy.colors.semantic.indicator.orange}
                      style={{ flexShrink: 0 }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 400,
                        lineHeight: "18px",
                        color: theme.nawy.colors.semantic.text.primary,
                      }}
                    >
                      {flag}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Slide>
    </Box>
  );
};

export default WhatsappThreadPanel;
